import React, { useState, useEffect } from 'react';
import api from '../../api/axios';

const EMOJI_MAP = { angry: '😠', sad: '😢', neutral: '😐', happy: '😊', excited: '🤩' };
const COLORS   = { angry: '#ef4444', sad: '#f97316', neutral: '#94a3b8', happy: '#22c55e', excited: '#8b5cf6' };
const SCORE    = { angry: 1, sad: 2, neutral: 3, happy: 4, excited: 5 };

function SentimentBar({ counts }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!total) return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No sentiment data</span>;
  return (
    <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', width: '100%', gap: 1 }}>
      {Object.entries(counts).map(([k, v]) =>
        v > 0 ? (
          <div key={k} title={`${k}: ${v}`} style={{ flex: v, background: COLORS[k], minWidth: 2 }} />
        ) : null
      )}
    </div>
  );
}

function OverallMood({ counts }) {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  if (!total) return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>;
  const avg = Object.entries(counts).reduce((sum, [k, v]) => sum + SCORE[k] * v, 0) / total;
  const rounded = Math.round(avg);
  const label = ['', 'angry', 'sad', 'neutral', 'happy', 'excited'][rounded] || 'neutral';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 18 }}>{EMOJI_MAP[label]}</span>
      <span style={{ fontSize: 11, color: COLORS[label], fontWeight: 600, textTransform: 'capitalize' }}>{label}</span>
    </span>
  );
}

function CustomerHistory({ customerId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/customers/${customerId}/history`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [customerId]);

  if (loading) return <div className="spinner" />;
  if (!data) return null;

  const { customer, responses } = data;

  return (
    <div className="card" style={{ marginTop: 16, borderLeft: '3px solid var(--primary)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h4 style={{ marginBottom: 4 }}>{customer.name || 'Anonymous'}</h4>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {customer.phone && <span>📱 {customer.phone}</span>}
            {customer.email && <span>📧 {customer.email}</span>}
            <span>🗓 {customer.totalFeedbacks} feedback{customer.totalFeedbacks !== 1 ? 's' : ''}</span>
            {customer.lastFeedbackAt && <span>Last: {new Date(customer.lastFeedbackAt).toLocaleDateString()}</span>}
          </div>
        </div>
        <button className="btn btn-outline btn-sm" onClick={onClose}>✕ Close</button>
      </div>

      {/* Sentiment timeline */}
      {responses.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.05em' }}>Sentiment timeline</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {responses.map((r, i) => (
              <div key={r._id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 20 }}>{r.overallSentiment ? EMOJI_MAP[r.overallSentiment] : (r.feedbackType === 'audio' ? '🎙️' : '📝')}</span>
                <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{new Date(r.createdAt).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Response list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {responses.map(r => (
          <div key={r._id} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 18 }}>{r.feedbackType === 'audio' ? '🎙️' : '📋'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {r.feedbackType === 'audio' ? 'Voice feedback' : 'Emoji feedback'}
                {r.billId && <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>· Bill: {r.billId}</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {new Date(r.createdAt).toLocaleString()}
                {r.feedbackTaker && <span> · Staff: {r.feedbackTaker.name}</span>}
              </div>
            </div>
            {r.overallSentiment && (
              <span style={{ fontSize: 18 }} title={r.overallSentiment}>{EMOJI_MAP[r.overallSentiment]}</span>
            )}
            {r.transcript && (
              <div style={{ width: '100%', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 4, paddingLeft: 30 }}>
                "{r.transcript.substring(0, 120)}{r.transcript.length > 120 ? '…' : ''}"
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState('');

  const load = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers${q ? `?search=${q}` : ''}`);
      setCustomers(data.data);
    } catch { setError('Failed to load customers'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    clearTimeout(window._searchTimer);
    window._searchTimer = setTimeout(() => load(e.target.value), 400);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this customer?')) return;
    try { await api.delete(`/customers/${id}`); load(search); } catch {}
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h3>Customers</h3>
          <p className="text-muted" style={{ fontSize: 13, marginTop: 3 }}>
            Customers who opted in to receive offers. Click any row to see their feedback history.
          </p>
        </div>
        {/*<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{customers.length} total</div>*/}
      </div>

      <input
        className="form-control"
        style={{ maxWidth: 320, marginBottom: 18 }}
        placeholder="🔍  Search by name, phone or email…"
        value={search}
        onChange={handleSearch}
      />

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? <div className="spinner" /> : customers.length === 0 ? (
        <div className="card text-center" style={{ padding: 56 }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>👥</div>
          <p className="text-muted">No customers yet. They appear when someone fills in the contact form on the feedback page.</p>
        </div>
      ) : (
        <div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Feedbacks</th>
                  <th>Overall mood</th>
                  <th>Sentiment breakdown</th>
                  <th>Last feedback</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <React.Fragment key={c._id}>
                    <tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedId(expandedId === c._id ? null : c._id)}
                    >
                      <td>
                        <strong>{c.name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No name</span>}</strong>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {c.phone && <div>📱 {c.phone}</div>}
                        {c.email && <div>📧 {c.email}</div>}
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{c.totalFeedbacks}</span>
                      </td>
                      <td><OverallMood counts={c.sentimentCounts} /></td>
                      <td style={{ minWidth: 120 }}><SentimentBar counts={c.sentimentCounts} /></td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {c.lastFeedbackAt ? new Date(c.lastFeedbackAt).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={e => { e.stopPropagation(); handleDelete(c._id); }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {expandedId === c._id && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 12px 12px', background: 'var(--bg)' }}>
                          <CustomerHistory customerId={c._id} onClose={() => setExpandedId(null)} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
